import { useEffect, useRef } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	createSingleQueryParamHook,
	createRouteParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';

import {
	SUGGESTIONS_PANEL_OPEN_QUERY_VALUE,
	SUGGESTIONS_PANEL_QUERY_PARAM,
} from './suggestionsPanelDeepLink.ts';
import { useShowSuggestionsPanel } from './useShowSuggestionsPanel.ts';
import { getVisibleSuggestions } from './suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from './useSuggestionsPanelFilters.ts';

const useContentId = createRouteParamHook('contentId');
const useSuggestionsPanelQueryParam = createSingleQueryParamHook(SUGGESTIONS_PANEL_QUERY_PARAM);

/**
 * Shows the Suggestions panel with the page's persisted suggestions when the editor is loaded with
 * `suggestionsPanel=open`, then removes the parameter so a reload does not show it again. Removing
 * the parameter is what prevents a repeat; there is no per-mount flag because live doc to live doc
 * navigation keeps this hook mounted, and the next doc's deep link must still work.
 *
 * The hook waits until collaborative editing has loaded the document, then refreshes the persisted
 * suggestions before showing the panel. The AI Suggestions plugin also loads them when its editor
 * view is created, but on an in-app navigation (for example from an Agent Session card on Home)
 * that load can run against a document that has not loaded yet and drop them as stale, leaving the
 * panel empty. Collab readiness covers both regular pages and live docs. The first visible suggestion
 * is selected as soon as suggestions for the current document are available, while refresh continues
 * in the background. User selection changes cancel the automatic selection.
 */
export const useShowSuggestionsPanelFromDeepLink = (): void => {
	const suggestionsPanelQueryParam = useSuggestionsPanelQueryParam();
	const contentId = useContentId();
	const { setQueryParams } = useRouteActions();
	const { showSuggestionsPanel, isSuggestionsPanelShown } = useShowSuggestionsPanel();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const [filters] = useSuggestionsPanelFilters();
	const { userId } = useSessionData();
	const selectionContext = useRef({ ...filters, currentUserId: userId });
	const pendingSelection = useRef<{ cancel: () => void; select: () => void } | undefined>();

	useEffect(() => {
		selectionContext.current = { ...filters, currentUserId: userId };
		pendingSelection.current?.select();
	}, [filters, userId]);

	// Removing the URL parameter must not cancel the pending selection. Cancel it
	// when the editor changes (including live-doc navigation) or the hook unmounts.
	useEffect(() => () => pendingSelection.current?.cancel(), [fullPageEditorPresetAPI, contentId]);
	useEffect(() => {
		if (!isSuggestionsPanelShown) {
			pendingSelection.current?.cancel();
		}
	}, [isSuggestionsPanelShown]);

	useEffect(() => {
		// The experiment is checked last so ordinary editor loads are not exposed to it.
		if (
			suggestionsPanelQueryParam !== SUGGESTIONS_PANEL_OPEN_QUERY_VALUE ||
			!fullPageEditorPresetAPI?.aiSuggestions ||
			!expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)
		) {
			return;
		}

		const { aiSuggestions, collabEdit } = fullPageEditorPresetAPI;
		let hasShown = false;
		let unsubscribe: (() => void) | undefined;

		const showWithPersistedSuggestions = () => {
			if (hasShown) {
				return;
			}
			hasShown = true;
			unsubscribe?.();
			pendingSelection.current?.cancel();
			let cancelled = false;
			let unsubscribeSelection: (() => void) | undefined;
			const cancel = () => {
				cancelled = true;
				unsubscribeSelection?.();
			};
			// A seamless live-doc transition can briefly retain the outgoing document's
			// suggestions. Only select data belonging to the destination page.
			const isCurrentDocument = (
				state: ReturnType<typeof aiSuggestions.sharedState.currentState>,
			) =>
				!!contentId &&
				!!state?.orderedSuggestionIds.some((id) =>
					state.suggestionById.get(id)?.contentAri.endsWith(`:page/${contentId}`),
				);
			const initialState = aiSuggestions.sharedState.currentState();
			const initialSelection = isCurrentDocument(initialState)
				? initialState?.selectedSuggestion
				: undefined;
			const selectFirstAvailableSuggestion = () => {
				const state = aiSuggestions.sharedState.currentState();
				if (cancelled || !state || !isCurrentDocument(state)) {
					return;
				}
				if (state.selectedSuggestion) {
					cancel();
					return;
				}
				const suggestions = state.orderedSuggestionIds.flatMap((id) => {
					const suggestion = state.suggestionById.get(id);
					return suggestion?.contentAri.endsWith(`:page/${contentId}`) ? [suggestion] : [];
				});
				const firstSuggestion = getVisibleSuggestions(suggestions, selectionContext.current)?.[0];
				if (firstSuggestion) {
					// Unsubscribe before selecting: selectSuggestion itself updates shared state.
					cancel();
					aiSuggestions.actions.selectSuggestion(firstSuggestion.id, {
						interactionPoint: 'sidebar',
					});
				}
			};
			unsubscribeSelection = aiSuggestions.sharedState.onChange(({ nextSharedState }) => {
				if (!nextSharedState || !isCurrentDocument(nextSharedState)) {
					return;
				}
				if (nextSharedState.selectedSuggestion !== initialSelection) {
					cancel();
					return;
				}
				// Let every subscriber receive this update before dispatching selection,
				// otherwise a panel listener can overwrite it with the older snapshot.
				void Promise.resolve().then(selectFirstAvailableSuggestion);
			});
			pendingSelection.current = { cancel, select: selectFirstAvailableSuggestion };
			const refresh = aiSuggestions.actions.refreshSuggestions();
			selectFirstAvailableSuggestion();
			void refresh
				.then((refreshed) => {
					// Also read the final snapshot in case shared-state notifications are debounced.
					if (refreshed) {
						selectFirstAvailableSuggestion();
					}
				})
				.finally(cancel);
			showSuggestionsPanel({ entryPoint: 'suggestionsPanel' });
			setQueryParams({ [SUGGESTIONS_PANEL_QUERY_PARAM]: null }, true);
		};

		if (!collabEdit || collabEdit.sharedState.currentState()?.initialised.collabInitialisedAt) {
			showWithPersistedSuggestions();
			return;
		}

		unsubscribe = collabEdit.sharedState.onChange(({ nextSharedState }) => {
			if (nextSharedState?.initialised.collabInitialisedAt) {
				showWithPersistedSuggestions();
			}
		});

		return () => {
			hasShown = true;
			unsubscribe?.();
		};
	}, [
		contentId,
		fullPageEditorPresetAPI,
		setQueryParams,
		showSuggestionsPanel,
		suggestionsPanelQueryParam,
	]);
};
